import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';

import { useAlertsStore } from '@tallo/design-system';
import { portfolioQueryKey, getPropertyStatisticsQueryKey } from '@tallo/investor/property';

import { EscalationRelaySkipReason } from '../interfaces/intelligent-escalation.interface';
import { getIntelligentEscalationsQueryKey } from '../queries/intelligent-escalations.query';
import { getPropertyIntelligentEscalationsQueryKey } from '../queries/property-intelligent-escalations.query';
import intelligentEscalationService from '../services/intelligent-escalations.service';

const skipReasonLabels: Record<EscalationRelaySkipReason, string> = {
  'ai-paused': 'Tallo is paused for them',
  'inquiry-closed': 'their inquiry is closed',
  'no-conversation': 'there is no conversation with them',
  'fair-housing': 'the answer did not pass the fair-housing check, try rewording it',
  'send-failed': 'sending failed, try again',
};

export const useAnswerIntelligentEscalation = defineMutation(() => {
  return useMutation({
    mutation(payload: { propertyId: string; escalationId: string; answer: string }) {
      return intelligentEscalationService.answer(payload.escalationId, payload.answer);
    },

    async onSuccess(result, payload) {
      const queryCache = useQueryCache();
      const alertsStore = useAlertsStore();

      queryCache.invalidateQueries({ key: getIntelligentEscalationsQueryKey() });
      queryCache.invalidateQueries({ key: getPropertyStatisticsQueryKey(payload.propertyId) });
      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
      await queryCache.invalidateQueries({ key: getPropertyIntelligentEscalationsQueryKey(payload.propertyId) });

      // The request succeeds even when no renter could be reached, so the result decides the toast.
      const notReached = result.skipped
        .map(({ renter, reason }) => `${renter}: ${skipReasonLabels[reason] ?? 'could not be reached'}`)
        .join('; ');

      if (!result.relayedTo.length) {
        alertsStore.error(`Your answer was not sent (${notReached}). The question is still open.`);
      } else if (result.skipped.length) {
        alertsStore.warning(`Answer sent to ${result.relayedTo.join(', ')}, but not to ${notReached}.`);
      } else {
        alertsStore.success('Escalation resolved');
      }
    },

    onError() {
      const alertsStore = useAlertsStore();

      alertsStore.error('Error resolving escalation');
    },
  });
});
